import {useQuery, UseQueryResult} from '@tanstack/react-query';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {FEED_LIST_PAGE_SIZE_DEFAULT_VALUES} from '../../../../constants';
import {IPresentation} from '../../../../incase-domain/presentation';
import {IPaginatedResult} from '../../../../incase-domain/search';
import {fetchPresentationFeed} from '../../../../service/presentation';
import PageHeader from '../../../components/common/header/PageHeader';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinnerComponent from '../../../components/LoadingSpinnerComponent';
import FeedList from '../../../components/presentationList/presentationFeed/components/FeedList';
import {useListParamsState} from '../../../../hooks/useListParamsState';

function HomeFeedPage(): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const {listParams, setListParams} = useListParamsState({
        size: FEED_LIST_PAGE_SIZE_DEFAULT_VALUES.initialPageSize,
    });
    const presentationsFeed: UseQueryResult<IPaginatedResult<IPresentation>> = useQuery<IPaginatedResult<IPresentation>>(
        QUERY_KEYS.feed.list(listParams),
        () =>
            fetchPresentationFeed({
                pageNumber: listParams.page,
                pageSize: listParams.size,
                searchString: listParams.search,
            })
    );

    return !presentationsFeed.isLoading ? (
        <>
            <PageHeader
                title={t('navigation.feed')}
                items={[
                    {
                        icon: 'solar:lock-linear',
                        label: t('navigation.personal'),
                        onClick: () => navigate('/presentations/'),
                    },
                    {
                        icon: 'solar:users-group-rounded-linear',
                        label: t('navigation.shared'),
                        onClick: () => navigate('/presentations/shared'),
                    },
                    {
                        icon: 'solar:posts-carousel-vertical-linear',
                        label: t('homePage.feed'),
                        onClick: () => {
                            // no-op
                        },
                        active: true,
                    },
                ]}
                defaultIndex={2}
            />
            {presentationsFeed.data && presentationsFeed.data?.content.length > 0 ? (
                <>
                    <div className="u-pt-4">
                        <FeedList presentationFeed={presentationsFeed.data} />
                    </div>

                    <div className="u-mt-4">
                        <Pagination
                            page={listParams.page}
                            onPageClick={(page) => setListParams({page})}
                            numberOfPages={Math.ceil(presentationsFeed.data.numberOfElements / listParams.size)}
                        />
                    </div>
                </>
            ) : (
                <div className="c-errorMessage">{listParams.search !== '' ? t('homePage.noFeedResult') : t('homePage.noFeed')}</div>
            )}
        </>
    ) : (
        <LoadingSpinnerComponent />
    );
}

export default HomeFeedPage;
